import { ResponseStatus, WidgetStyle } from '@sdbn/appclient';
import { ResponseMessage } from '@sdbn/appclient';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { ConfirmDialogData, ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { NgForm } from '@angular/forms';
import { Component, OnInit, ViewChild } from '@angular/core';

import { TranslationService } from '@sdbn/common';
import { Station } from '../model/station.model';
import { StationService } from '../service/station.service';
import { ContextChange } from '@sdbn/appclient';
import { MasterDetailListEntry } from '@sdbn/common';
import { ValidationMessageType } from '@sdbn/appclient';
import { ResponseMessageType } from '@sdbn/appclient';
import { AbstractWidgetComponent } from '@sdbn/appclient';

@Component({
  selector: 'app-station',
  templateUrl: './station.component.html',
  styleUrls: ['./station.component.scss']
})

export class StationComponent extends AbstractWidgetComponent implements OnInit {

  isLoading = false;

  stations: Station[];

  responseMessages: ResponseMessage[];

  selectedStation: Station;
  workingCopyDetails: Station = new Station();

  @ViewChild('stationForm') stationForm: NgForm;

  constructor(
    private stationService: StationService,
    public translationService: TranslationService,
    private dialog: MatDialog) {

    super();

  }

  ngOnInit() {
    this.load();
  }

  getValidationTypeByMessageType(messageType: ResponseMessageType): ValidationMessageType {
    if (messageType === ResponseMessageType.FAILURE) {
      return ValidationMessageType.ERROR;
    } else if (messageType === ResponseMessageType.WARNING) {
      return ValidationMessageType.WARNING;
    } else {
      return ValidationMessageType.INFO;
    }
  }

  private load() {
    this.isLoading = true;
    this.responseMessages = [];

    this.stationService.getStations().subscribe(response => {
      this.stations = response;
      if (this.stations.length > 0) {
        if (this.selectedStation == null) {
          this.setSelectedWithoutCheck(this.stations[0]);
        } else {
          const findSelected = this.stations.find(s => s.id === this.selectedStation.id);
          this.setSelectedWithoutCheck(findSelected == null ? new Station() : findSelected);
        }
      }

      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  setSelectedStation(station: Station) {

    if (this.selectedStation != null && this.selectedStation.id === station.id) {
      return;
    }

    if (this.stationForm != null && this.stationForm.form.dirty) {
      this.selectedStation = this.selectedStation;
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.stations.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.stations.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(station);
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(station);
    }
  }

  private setSelectedWithoutCheck(station: Station) {
    this.responseMessages = [];

    this.selectedStation = station;
    this.workingCopyDetails = this.copyStation(this.selectedStation);
    if (this.stationForm) {
      this.stationForm.resetForm(this.workingCopyDetails);
    }
  }

  isSelected(station: Station) {
    return this.selectedStation != null && this.selectedStation.id === station.id;
  }

  delete(station: Station) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.admin.stations.delete.confirm-dialog.title',
      messageKey: 'tmc.admin.stations.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.stationService.deleteStation(station).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.isLoading = false;
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  addNew() {
    if (this.stationForm.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.stations.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.stations.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.stationForm.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.stations.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.stations.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  clear() {
    this.responseMessages = [];
    this.selectedStation = new Station();
    this.workingCopyDetails = this.copyStation(this.selectedStation);
    this.stationForm.resetForm(this.workingCopyDetails);
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }


  private discardChanges() {
    this.responseMessages = [];
    if (this.workingCopyDetails.id != null) {
      this.setSelectedWithoutCheck(this.selectedStation);
    } else {
      this.workingCopyDetails = new Station();
      this.stationForm.resetForm(this.workingCopyDetails);
    }
  }

  save() {
    this.isLoading = true;
    this.responseMessages = [];
    if (this.workingCopyDetails.id > 0) {
      this.stationService.updateStation(this.workingCopyDetails).subscribe(
        response => {
          this.isLoading = false;
          if (response.status === ResponseStatus.SUCCESS) {
            this.load();
          } else {
            this.responseMessages = response.responseMessages;
          }
        },
        error => {
          console.log('Error while updating station!', error);
          this.isLoading = false;
        });
    } else {
      this.stationService.createStation(this.workingCopyDetails).subscribe(
        response => {
          this.clearAndLoad();
          this.isLoading = false;
        },
        error => {
          console.log('Error while creating station!', error);
          this.isLoading = false;
        });
    }
  }

  private copyStation(source: Station): Station {
    const target = new Station(source);
    return target;
  }

  get cancelEnabled() {
    return !this.isLoading && this.stationForm != null && this.stationForm.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.stationForm != null && this.stationForm.dirty && this.stationForm.valid;
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelectedStation(entry as Station);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as Station);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
